import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FaArrowLeft, FaCopy, FaCheck } from "react-icons/fa";

export default function EmbedCode() {
  const [copied, setCopied] = useState(null);
  
  const BASE_URL = window.location.origin;

  const iframeCode = `<iframe 
  src="${BASE_URL}/embed" 
  width="100%" 
  height="800" 
  frameborder="0" 
  style="border: none; background: #030303;"
  title="Underplay Recordings Releases"
></iframe>`;

  const scriptCode = `<!-- Underplay Recordings Widget -->
<div id="underplay-releases-widget"></div>
<script>
  (function() {
    var iframe = document.createElement('iframe');
    iframe.src = '${BASE_URL}/embed';
    iframe.width = '100%';
    iframe.height = '800';
    iframe.frameBorder = '0';
    iframe.style.border = 'none';
    iframe.style.background = '#030303';
    iframe.title = 'Underplay Recordings Releases';
    document.getElementById('underplay-releases-widget').appendChild(iframe);
  })();
</script>`;

  const directLinkCode = `${BASE_URL}`;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="embed-code-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              <FaArrowLeft className="mr-2" /> Back
            </Button>
          </Link>
          <h1 className="text-xl font-syne font-bold text-white">Embed Widget</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Preview */}
        <div className="bg-background-surface border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-syne font-bold text-white mb-4">Preview</h2>
          <div className="aspect-video rounded-lg overflow-hidden border border-zinc-700">
            <iframe 
              src={`${BASE_URL}/embed`}
              width="100%" 
              height="100%" 
              frameBorder="0"
              title="Widget Preview"
              style={{ background: '#030303' }}
            />
          </div>
        </div>

        {/* Direct Link */}
        <div className="bg-background-surface border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-syne font-bold text-white mb-2">Option 1: Direct Link</h2>
          <p className="text-sm text-zinc-400 mb-4">Share this link directly or redirect to it from your website.</p>
          <div className="relative">
            <pre className="bg-zinc-900 rounded-lg p-4 text-sm text-zinc-300 overflow-x-auto">
              {directLinkCode}
            </pre>
            <Button
              size="sm"
              onClick={() => copyToClipboard(directLinkCode, 'link')}
              className="absolute top-2 right-2 bg-zinc-800 hover:bg-zinc-700"
            >
              {copied === 'link' ? <FaCheck className="mr-1" /> : <FaCopy className="mr-1" />}
              {copied === 'link' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        {/* iFrame Embed */}
        <div className="bg-background-surface border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-syne font-bold text-white mb-2">Option 2: iFrame Embed</h2>
          <p className="text-sm text-zinc-400 mb-4">Copy and paste this code into your website's HTML.</p>
          <div className="relative">
            <pre className="bg-zinc-900 rounded-lg p-4 text-sm text-zinc-300 overflow-x-auto whitespace-pre-wrap">
              {iframeCode}
            </pre>
            <Button
              size="sm"
              onClick={() => copyToClipboard(iframeCode, 'iframe')}
              className="absolute top-2 right-2 bg-zinc-800 hover:bg-zinc-700"
            >
              {copied === 'iframe' ? <FaCheck className="mr-1" /> : <FaCopy className="mr-1" />}
              {copied === 'iframe' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        {/* Script Embed */}
        <div className="bg-background-surface border border-zinc-800 rounded-xl p-6">
          <h2 className="text-lg font-syne font-bold text-white mb-2">Option 3: JavaScript Embed</h2>
          <p className="text-sm text-zinc-400 mb-4">Add this script anywhere on your page where you want the widget to appear.</p>
          <div className="relative">
            <pre className="bg-zinc-900 rounded-lg p-4 text-sm text-zinc-300 overflow-x-auto whitespace-pre-wrap">
              {scriptCode}
            </pre>
            <Button
              size="sm"
              onClick={() => copyToClipboard(scriptCode, 'script')}
              className="absolute top-2 right-2 bg-zinc-800 hover:bg-zinc-700"
            >
              {copied === 'script' ? <FaCheck className="mr-1" /> : <FaCopy className="mr-1" />}
              {copied === 'script' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-sm font-syne font-bold text-white mb-3">Tips</h3>
          <ul className="text-sm text-zinc-400 space-y-2">
            <li>• Adjust the <code className="text-zinc-300 bg-zinc-800 px-1 rounded">height</code> value to fit your page layout</li>
            <li>• The widget is responsive and will adapt to the container width</li>
            <li>• Use <code className="text-zinc-300 bg-zinc-800 px-1 rounded">/embed</code> for a minimal version without header/footer</li>
            <li>• The main URL shows the full page with navigation</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
